import React, { useState, useEffect } from 'react';
import { Send, Mail, MessageSquare, Phone, Calendar, FileText, Users, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import { getAuthToken } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

interface Communication {
  communicationId: string;
  recipientType: string;
  recipientId: string;
  channel: string;
  subject: string;
  message: string;
  status: string;
  deliveryStatus: string;
  sentAt: any;
  sentBy: string;
  scheduledFor?: any;
}

interface Template {
  templateId: string;
  name: string;
  category: string;
  channel: string;
  subject: string;
  body: string;
  variables: string[];
  usageCount: number;
  isActive: boolean;
}

const CommunicationCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'send' | 'history' | 'templates' | 'scheduled'>('send');
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [scheduledComms, setScheduledComms] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Send form state
  const [sendForm, setSendForm] = useState({
    recipientType: 'lead',
    recipientId: '',
    channel: 'email',
    subject: '',
    message: '',
    templateId: '',
    scheduledFor: ''
  });

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'follow-up',
    channel: 'email',
    subject: '',
    body: '',
    variables: [] as string[]
  });

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchCommunicationHistory();
    } else if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'scheduled') {
      fetchScheduledCommunications();
    }
  }, [activeTab]);

  const fetchCommunicationHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/communications/history`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setCommunications(response.data.data || []);
    } catch (error) {
      console.error('Error fetching communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/communications/templates`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledCommunications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/communications/scheduled`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setScheduledComms(response.data.data || []);
    } catch (error) {
      console.error('Error fetching scheduled communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCommunication = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_URL}/communications/send`, sendForm, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      alert('Communication sent successfully!');
      setSendForm({
        recipientType: 'lead',
        recipientId: '',
        channel: 'email',
        subject: '',
        message: '',
        templateId: '',
        scheduledFor: ''
      });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send communication');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingTemplate) {
        await axios.put(
          `${API_URL}/communications/templates/${editingTemplate.templateId}`,
          templateForm,
          { headers: { Authorization: `Bearer ${await getAuthToken()}` } }
        );
        alert('Template updated successfully!');
      } else {
        await axios.post(`${API_URL}/communications/templates`, templateForm, {
          headers: { Authorization: `Bearer ${await getAuthToken()}` }
        });
        alert('Template created successfully!');
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        category: 'follow-up',
        channel: 'email',
        subject: '',
        body: '',
        variables: []
      });
      fetchTemplates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API_URL}/communications/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      alert('Template deleted successfully!');
      fetchTemplates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category,
      channel: template.channel,
      subject: template.subject,
      body: template.body,
      variables: template.variables
    });
    setShowTemplateModal(true);
  };

  const handleUseTemplate = (template: Template) => {
    setSendForm({
      ...sendForm,
      channel: template.channel,
      subject: template.subject,
      message: template.body,
      templateId: template.templateId
    });
    setActiveTab('send');
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'whatsapp': return <Phone className="w-4 h-4" />;
      default: return <Send className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      sent: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Communication Center</h1>
        <p className="text-gray-600">Send messages, manage templates, and track communications</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('send')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'send'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Send className="w-4 h-4 inline mr-2" />
            Send Message
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            History
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scheduled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Scheduled
          </button>
        </nav>
      </div>

      {/* Send Message Tab */}
      {activeTab === 'send' && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Send Communication</h2>
          <form onSubmit={handleSendCommunication} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Type
                </label>
                <select
                  value={sendForm.recipientType}
                  onChange={(e) => setSendForm({ ...sendForm, recipientType: e.target.value })}
                  className="input"
                  required
                >
                  <option value="lead">Lead</option>
                  <option value="parent">Parent</option>
                  <option value="student">Student</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient ID
                </label>
                <input
                  type="text"
                  value={sendForm.recipientId}
                  onChange={(e) => setSendForm({ ...sendForm, recipientId: e.target.value })}
                  className="input"
                  placeholder="Enter recipient ID"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel
                </label>
                <select
                  value={sendForm.channel}
                  onChange={(e) => setSendForm({ ...sendForm, channel: e.target.value })}
                  className="input"
                  required
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="in-app">In-App</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule For (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={sendForm.scheduledFor}
                  onChange={(e) => setSendForm({ ...sendForm, scheduledFor: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject {sendForm.channel === 'email' && '(Required for Email)'}
              </label>
              <input
                type="text"
                value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                className="input"
                placeholder="Enter subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                className="input"
                rows={6}
                placeholder="Enter your message"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setActiveTab('templates')}
                className="btn-secondary"
              >
                Use Template
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                <Send className="w-4 h-4 mr-2" />
                {sendForm.scheduledFor ? 'Schedule' : 'Send'} Message
              </button>
            </div>
          </form>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Communication History</h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : communications.length === 0 ? (
            <p className="text-gray-500">No communications found</p>
          ) : (
            <div className="space-y-3">
              {communications.map((comm) => (
                <div key={comm.communicationId} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getChannelIcon(comm.channel)}
                        <span className="font-medium">{comm.subject || 'No Subject'}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(comm.status)}`}>
                          {comm.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{comm.message.substring(0, 100)}...</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>To: {comm.recipientId}</span>
                        <span>Channel: {comm.channel}</span>
                        <span>Sent: {comm.sentAt ? new Date(comm.sentAt).toLocaleString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Message Templates</h2>
            <button
              onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({
                  name: '',
                  category: 'follow-up',
                  channel: 'email',
                  subject: '',
                  body: '',
                  variables: []
                });
                setShowTemplateModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </button>
          </div>

          <div className="card">
            {loading ? (
              <p className="text-gray-500">Loading...</p>
            ) : templates.length === 0 ? (
              <p className="text-gray-500">No templates found</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div key={template.templateId} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium">{template.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {template.category}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded flex items-center">
                            {getChannelIcon(template.channel)}
                            <span className="ml-1">{template.channel}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Use Template"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.templateId)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                    <p className="text-xs text-gray-500">{template.body.substring(0, 100)}...</p>
                    <p className="text-xs text-gray-400 mt-2">Used {template.usageCount} times</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Scheduled Communications</h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : scheduledComms.length === 0 ? (
            <p className="text-gray-500">No scheduled communications</p>
          ) : (
            <div className="space-y-3">
              {scheduledComms.map((comm) => (
                <div key={comm.communicationId} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium">{comm.subject || 'No Subject'}</span>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          Scheduled
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{comm.message.substring(0, 100)}...</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>To: {comm.recipientId}</span>
                        <span>Channel: {comm.channel}</span>
                        <span>Scheduled: {comm.scheduledFor ? new Date(comm.scheduledFor).toLocaleString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </h2>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="welcome">Welcome</option>
                    <option value="follow-up">Follow-up</option>
                    <option value="reminder">Reminder</option>
                    <option value="notification">Notification</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel
                  </label>
                  <select
                    value={templateForm.channel}
                    onChange={(e) => setTemplateForm({ ...templateForm, channel: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="in-app">In-App</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Body
                </label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  className="input"
                  rows={6}
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationCenter;
